import { Loader2, ClipboardCopy, CheckCheck, RefreshCw } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { MacSuggestion, CopilotMode } from './types';
import { MODE_LABELS } from './types';

interface Props {
  suggestions: MacSuggestion[];
  onUse: (suggestion: MacSuggestion, idx: number) => void;
  onRegenerate: (idx: number) => void;
  onCopy: (text: string) => void;
  regeneratingIdx: number | null;
}

export function CopilotVariantsList({ suggestions, onUse, onRegenerate, onCopy, regeneratingIdx }: Props) {
  if (!suggestions.length) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-shell-border" />
        <span className="text-xs font-medium text-shell-muted px-2">Варианты</span>
        <div className="h-px flex-1 bg-shell-border" />
      </div>

      {suggestions.map((s, idx) => (
        <div
          key={s.id}
          className="rounded-xl border border-shell-border bg-shell-surface p-3 space-y-2 transition-all hover:border-violet-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-shell-muted">
              Вариант {idx + 1}
              {s.mode ? ` · ${MODE_LABELS[s.mode as CopilotMode] ?? s.mode}` : ''}
              {s.model ? ` · ${s.model}` : ''}
            </span>
          </div>

          <p className="text-sm text-shell-strong leading-relaxed line-clamp-4 whitespace-pre-wrap">
            {s.text}
          </p>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => onUse(s, idx)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                'bg-violet-600 text-white hover:bg-violet-700 active:scale-95'
              )}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Использовать
            </button>

            <button
              onClick={() => onCopy(s.text)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-shell-muted hover:text-shell-strong hover:bg-shell-hover transition-all"
            >
              <ClipboardCopy className="w-3.5 h-3.5" />
              Скопировать
            </button>

            <button
              onClick={() => onRegenerate(idx)}
              disabled={regeneratingIdx !== null}
              className="ml-auto flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-shell-muted hover:text-violet-600 hover:bg-violet-50 transition-all disabled:opacity-40"
              title="Регенерировать только этот вариант"
            >
              {regeneratingIdx === idx
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5" />
              }
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
